// libs
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// utils
import DateFormated from "../../Utils/Date/Date";
import DateFilters, { TODAY, TODAY_MINUS_14, TODAY_MINUS_30 } from "../../Accounts/Account/Tables/DateFilters";
import Export from "../../Utils/XLS/Export";

// components
import ReadingsTable from "../Table/ReadingsTable";
import Sorter from "../../UI/Toolbar/Sorter";
import Spinner from "../../UI/Spinner";
import Alert from "../../UI/Alerts/Alert";

const Statistics = (props) => {
	const { t, i18n } = useTranslation();

	const initialSortField = 'lastRead';
	///////////////////////////////////////////////////////////////////////////////////
	const [sortedData, setSortedData] = useState([]);
	const [checked, setChecked] = useState([]);
	const [isSelectAll, setIsSelectAll] = useState(false);
	const [sortTableField, setSortTableField] = useState(initialSortField);
	const [sortTableDirection, setSortTableDirection] = useState('DESC');
	const [sortBy, setSortBy] = useState(null);
	///////////////////////////////////////////////////////////////////////////////////

	const dataWithInventory = (props.data || []).map(data => {
		const { lastLevel, inventory } = data;

		// Calculate the currentInventory based on the percentage
		const currentInventory = Math.round(inventory.value * lastLevel);

		// Add the new property to the object
		return { ...data, currentInventory };
	});

	useMemo(() => {
		if(dataWithInventory && dataWithInventory.length){
			setSortedData(dataWithInventory);
		}
	}, []);

	//console.log('dataWithInventory',dataWithInventory);

	const formatDateYMDHM = (date) => {
		if(!date) return '';
		const lineDate = new Date(date);

		const year = lineDate.getFullYear();
		const month = (lineDate.getMonth() + 1).toString().padStart(2, '0');
		const day = lineDate.getDate().toString().padStart(2, '0');
		const hours = lineDate.getHours().toString().padStart(2, '0');
		const minutes = lineDate.getMinutes().toString().padStart(2, '0');

		return `${year}-${month}-${day} ${hours}:${minutes}`;
	};


	const calculatePercentage = (value) => {
		return value ? Math.round(parseFloat(value) * 100) + '%' : '0%';
	};

	const doXLSExport = useCallback((fileType) => {
		const checkedRows = dataWithInventory
			.filter((reading) =>
				checked.includes(reading.dipReadingExternalId.toString())
			)
			.map((reading) => ({
				[t("tanks.details.data.statistics.table.date")]: formatDateYMDHM(reading.lastRead),
				[t("tanks.details.data.statistics.table.level")]: calculatePercentage(reading.lastLevel),
				[t("tanks.details.data.statistics.table.consumption")]: parseFloat(reading.last24HourConsumption).toFixed(2) + ' L',
				[t("tanks.details.data.statistics.table.inventory")]: parseInt(reading.correctedVolume) + ' L',
				[t("tanks.details.data.statistics.table.ullage")]: parseInt(reading.ullage) + ' L',
				[t("tanks.details.data.statistics.table.temperature")]: parseInt(reading.temperature) + ' ' + t("tanks.details.misc.degrees_celsius"),
				[t("tanks.details.data.statistics.table.signal_strength")]: parseInt(reading.signalStrength) + '%',
				[t("tanks.details.data.statistics.table.issues")]: reading.alarmSent ? t("tanks.details.misc.issues") : t("tanks.details.misc.no_issues"),
			}));
		Export(checkedRows, 'harnois_export', [], fileType);
	}, [checked, dataWithInventory, i18n]);


	const sorterValues = [
		{
			label: t("tanks.details.data.statistics.table.date"),
			id: 'lastRead',
			value: 'lastRead',
			type: 'date'
		},{
			label: t("tanks.details.data.statistics.table.level"),
			id: 'lastLevel',
			value: 'lastLevel',
			type: 'float'
		},{
			label: t("tanks.details.data.statistics.table.consumption"),
			id: 'last24HourConsumption',
			value: 'last24HourConsumption',
			type: 'float'
		},{
			label: t("tanks.details.data.statistics.table.ullage"),
			id: 'ullage',
			value: 'ullage',
			type: 'int'
		},{
			label: t("tanks.details.data.statistics.table.temperature"),
			id: 'temperature',
			value: 'temperature',
			type: 'float'
		},{
			label: t("tanks.details.data.statistics.table.signal_strength"),
			id: 'signalStrength',
			value: 'signalStrength',
			type: 'int'
		},{
			label: t("tanks.details.data.statistics.table.issues"),
			id: 'readingTrouble',
			value: 'readingTrouble',
			type: 'string'
		},
	];

	const columns = useMemo(
		() => [
			{
				Header: (
					<div className="flex flex-row justify-content-end align-items-end">
						<input
							type="checkbox"
							id="select_all"
							className=""
							value={isSelectAll}
							onChange={(v) => setIsSelectAll(v.target.checked)}
						></input>
					</div>
				),
				accessor: "dipReadingExternalId",
				Cell: (props) => {
					return (
						<div className="flex flex-row justify-content-end">
							<input
								type="checkbox"
								value={props.value}
								className=""
								onChange={(v) => {
									handleCheck(
										v.target.checked,
										v.target.value
									);
								}}
								checked={!!checked.includes(props.value.toString())}
							></input>
						</div>
					);
				},
				disableSortBy: true,
			},
			{
				Header: t("tanks.details.data.statistics.table.date"),
				accessor: "lastRead",
				sort: "desc",
				Cell: (props) => {
					return <DateFormated date={props.value} withTime={true} />;
				},
				sortMethod: (a, b) => {
					var a1 = new Date(a).getTime();
					var b1 = new Date(b).getTime();
					if (a1 < b1) return 1;
					else if (a1 > b1) return -1;
					else return 0;
				}
			},
			{
				Header: t("tanks.details.data.statistics.table.level"),
				accessor: "lastLevel",
				Cell: (props) => {
					const percentage = (props.value) ? Math.round((parseFloat(props.value) * 100)) : 0;
					return <span>{percentage}%</span>;
				}
			},
			{
				Header: t("tanks.details.data.statistics.table.consumption"),
				accessor: "last24HourConsumption",
				Cell: (props) => {
					return (<span>{parseFloat(props.value).toFixed(2)} L</span>);
				},
			},
			{
				Header: t("tanks.details.data.statistics.table.inventory"),
				accessor: "correctedVolume",
				Cell: (props) => {
					return (<span>{ props.value + ' L'}</span>);
				},
			},
			{
				Header: t("tanks.details.data.statistics.table.ullage"),
				accessor: "ullage",
				Cell: (props) => {
					return (<span>{Math.round(parseFloat(props.value))} L</span>);
				},
			},
			{
				Header: t("tanks.details.data.statistics.table.temperature"),
				accessor: "temperature",
				Cell: (props) => {
					return (<span>{Math.round(parseFloat(props.value))} {t("tanks.details.misc.degrees_celsius")}</span>);
				},
			},
			{
				Header: t("tanks.details.data.statistics.table.signal_strength"),
				accessor: "signalStrength",
				Cell: (props) => {
					return (<span>{parseInt(props.value)}%</span>);
				},
			},
			{
				Header: t("tanks.details.data.statistics.table.issues"),
				accessor: "readingTrouble",
				Cell: (props) => {
					return (
						<span>
							{
								(props.value)
									? t("tanks.details.misc.issues")
									: t("tanks.details.misc.no_issues")
							}
						</span>
					);
				},
			},
		],
		[isSelectAll, checked, t, sortedData]
	);

	useEffect(
		() =>
			isSelectAll
				? setChecked(dataWithInventory.map((reading) => reading.dipReadingExternalId.toString()))
				: setChecked([]),
		[isSelectAll]
	);


	const handleCheck = (isChecked, value) => {
		if (isChecked && !checked.includes(value.toString())) {
			setChecked((checked) => [
				...checked.filter((thisChecked) => thisChecked !== value),
				value,
			]);
		} else {
			setChecked((checked) =>
				checked.filter((thisChecked) => thisChecked !== value)
			);
		}
	};

	const storeSortedItems = (items) => {
		if (items.length > 0) {
			setSortedData(items);
		}
	}

	const setSorterCallback = (columnId, direction) => {
		setSortTableField(columnId);
		setSortTableDirection(direction);
	}

	useEffect(() => {
		if (sortBy && sortBy.length > 0) {
			setSortTableField(sortBy[0].id);
			setSortTableDirection(sortBy[0].desc ? 'DESC' : 'ASC')
		}
	}, [sortBy])

	const setSortByCallback = useMemo(() =>
			[{desc: sortTableDirection === 'DESC', id: sortTableField}]
		, [sortTableDirection, sortTableField])

	useEffect(() => {
		if (setSortByCallback.length) {
			setSortBy(setSortByCallback)
		}
	}, [setSortByCallback]);

	let dataTitle = '';
	switch(props.dateConfigs.dateFilterType){
		case "last_14":
			dataTitle = t('tanks.details.data.statistics.data_title_14');
			break;
		case "last_30":
			dataTitle = t('tanks.details.data.statistics.data_title_30');
			break;
	}
	if(props.dateConfigs.isSpecific === true){
		dataTitle = t('tanks.details.data.statistics.data_title_specific');
	}

	let totalItems = 0;
	if(dataWithInventory && Array.isArray(dataWithInventory)){
		totalItems = dataWithInventory.length;
	}

	return (
		<>
			<h2 className="headline headline--section headline--uppercase">
				{t("tanks.details.tabs.stats")}
			</h2>
			<div className="flex top-actions top-actions--early mb-4">
				<DateFilters
					startDate={props.dateConfigs.startDate}
					endDate={props.dateConfigs.endDate}
					setStartDate={props.setStartDate}
					setEndDate={props.setEndDate}
					changeDateFilterType={props.changeDateFilterType}
					dateFilterType={props.dateConfigs.dateFilterType}
					component="statistics"
				/>
				<Sorter
					sortOptions={sorterValues}
					items={dataWithInventory}
					getSortedItems={storeSortedItems}
					setSortByCallback={setSorterCallback}
					sortDirection={sortTableDirection}
					sortField={sortTableField}
				/>
			</div>
			<h3 className="headline headline--sub mb-3">{ totalItems + " " + dataTitle }</h3>
			{
				(props.loading)
					? (<Spinner /> )
					: ((dataWithInventory && dataWithInventory.length)
						? (
							<ReadingsTable
								columns={columns}
								data={dataWithInventory}
								pageCount={props.totalPages}
								checked={checked}
								doXLSExport={doXLSExport}
								getSortedItems={storeSortedItems}
								setSortByCallback={setSortByCallback}
								sorterValues={sorterValues}
								showExportXls={true}
								sortField={sortTableField}
								sortDirection={sortTableDirection}
							/>
						) : (
							<Alert type="info" item={{
								type: 'info',
								additionalClasses: 'message--condensed',
								customIcon: 'fas fa-question-circle',
								text: 'tanks.details.data.no_result'
							}}/>
						)
					)
			}
		</>
	);
};

export default Statistics;
